import { CourseFormState } from '@/client/courses/types/courses.type';
import { EditorMainContent } from '@/components/content-editor/EditorMainContent';
import { EditorModalDialog } from '@/components/content-editor/EditorModalDialog';
import { EditorModalLayout } from '@/components/content-editor/EditorModalLayout';
import { UnsavedChangesModal } from '@/components/courses/modals/UnsavedChangesModal';
import { SlideTemplate } from '@/components/slides/admin/slide-templates/SlideTemplate';
import { SlideDeckSidebar } from '@/components/slides/admin/SlideDeckSidebar';
import { SlideTimelineCard } from '@/components/slides/admin/SlideTimelineCard';
import { useSlideDeckState } from '@/components/slides/admin/useSlideDeckState';
import { SlidesNavigation } from '@/components/slides/SlidesNavigation';
import { AppButton } from '@/ui/buttons';
import { EditableText } from '@/ui/editable-text/EditableText';
import { FlexContainer } from '@/ui/styled-ui';
import { PencilIcon } from '@/utils/helpers';
import { DndContext } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import { DialogProps } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

const MainContent = styled(EditorMainContent)<{ isPurpleBackground?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  ${({ isPurpleBackground }) =>
    isPurpleBackground &&
    css`
      background: var(--purple-dark);
      color: var(--white-main);
    `}

  h1 {
    margin-bottom: var(--medium-padding);
  }
`;

const AddSlideCard = styled(FlexContainer)`
  width: auto;
  background: var(--white-main);
  border-radius: var(--xsmall-border-radius);
  padding: var(--medium-padding) var(--small-padding);
  border: 2px solid var(--gray-dark);
  color: var(--black-main);
  cursor: pointer;
  font-size: var(--xsmall-font-size);
  box-shadow: none;
  margin-right: 0;
  min-height: 150px;
  margin-bottom: 0;
  flex-shrink: 0;

  &:hover {
    background: var(--gray-lighter);
  }

  .add-icon {
    display: flex;
    font-size: var(--medium-font-size);
    line-height: 1;
  }
`;

const TimelineContainer = styled.div`
  padding: var(--default-padding) 0 var(--medium-padding) 0;
  background: var(--white-main);

  > div {
    border-radius: var(--medium-border-radius);
    border: 1px solid var(--gray-dark);
    padding: 48px;
  }
`;

const TimelineLabel = styled.div`
  font-weight: 500;
  margin-bottom: var(--xsmall-padding);
`;

type ContentSlideDeckModalProps = {
  index: number;
  values: CourseFormState;
  setFieldValue: (field: string, value: any) => void;
  visible: boolean;
  onHide: () => void;
} & DialogProps;

export const ContentSlideDeckModal: React.FC<ContentSlideDeckModalProps> = ({
  visible,
  onHide,
  index,
  values,
  setFieldValue,
  ...dialogProps
}) => {
  const { t } = useTranslation();

  const {
    sensors,
    slides,
    mainSlides,
    popUpSlides,
    currentSlideData,
    currentEditingSlide,
    selectedLanguage,
    currentSlideIdx,
    setCurrentSlideIdx,
    selectedSlideId,
    setSelectedSlideId,
    isEditingPopUpSlide,
    setIsEditingPopUpSlide,
    isAnyDragging,
    isPurpleBackground,
    isSaveDisabled,
    loadFailed,
    retryLoad,
    showUnsavedChangesModal,
    setShowUnsavedChangesModal,
    presentationTitle,
    setPresentationTitle,
    handleClose,
    handleDiscard,
    handleSave,
    handleLanguageChange,
    handlePrevSlide,
    handleNextSlide,
    handleCopyToLanguage,
    handleSlidesDragStart,
    handleSlidesDragEnd,
    handleSlidesDragCancel,
    addSlide,
    deleteSlide,
    updateSlide,
    isLoading,
  } = useSlideDeckState({ index, values, visible, setFieldValue, onHide });

  const dialogHeaderTemplate = () => (
    <FlexContainer wrap="wrap">
      <h1 className="flex-1 w-full" translate="no">
        <EditableText
          key={presentationTitle}
          value={presentationTitle}
          icon={<PencilIcon />}
          saveValue={(value: string) => setPresentationTitle(value)}
        />
      </h1>
    </FlexContainer>
  );

  return (
    <EditorModalDialog
      visible={visible}
      onHide={handleClose}
      header={dialogHeaderTemplate}
      draggable={false}
      blockScroll
      {...dialogProps}
      closeOnEscape={false}
    >
      {isLoading && (
        <FlexContainer
          justify="center"
          align="center"
          style={{ minHeight: 300 }}
        >
          <ProgressSpinner />
        </FlexContainer>
      )}

      {!isLoading && loadFailed && (
        <FlexContainer
          direction="column"
          justify="center"
          align="center"
          gap={16}
          style={{ minHeight: 300 }}
        >
          <p>{t('state.provider.no.state')}</p>
          <AppButton label={t('button.tryAgain')} onClick={retryLoad} />
        </FlexContainer>
      )}

      {!isLoading && !loadFailed && (
        <>
          <EditorModalLayout translate="no">
            <MainContent isPurpleBackground={isPurpleBackground}>
              <SlideTemplate
                key={`${selectedLanguage}-${
                  currentEditingSlide?.id
                }-${currentSlideIdx}-${
                  isEditingPopUpSlide ? 'popup' : 'main'
                }-${currentSlideData?.templateType || 'text-image'}`}
                currentEditingSlide={currentEditingSlide}
                updateSlide={updateSlide}
                selectedLanguage={selectedLanguage}
                isEditingPopUpSlide={isEditingPopUpSlide}
                currentSlideIdx={currentSlideIdx}
                slides={slides}
                currentSlideData={currentSlideData}
                totalSlides={mainSlides.length}
              />
              <SlidesNavigation
                currentSlideIdx={currentSlideIdx}
                totalSlides={mainSlides.length}
                isEditingPopUpSlide={isEditingPopUpSlide}
                isEditMode
                attemptLanguage={selectedLanguage}
                onPrevSlide={handlePrevSlide}
                onNextSlide={handleNextSlide}
              />
            </MainContent>

            <SlideDeckSidebar
              selectedLanguage={selectedLanguage}
              courseLanguages={values.courseLanguages || []}
              currentSlideData={currentSlideData}
              popUpSlides={popUpSlides}
              isEditingPopUpSlide={isEditingPopUpSlide}
              isCustomContent={!!values.customAccount}
              onLanguageChange={handleLanguageChange}
              onSlideUpdate={updateSlide}
              onCopyToLanguage={handleCopyToLanguage}
              onSave={handleSave}
              isSaveDisabled={isSaveDisabled}
            />
          </EditorModalLayout>

          {/* Slides timeline */}
          <TimelineContainer translate="no">
            <div>
              <TimelineLabel>{t('content.slides.timeline')}</TimelineLabel>
              <DndContext
                modifiers={[restrictToHorizontalAxis]}
                sensors={sensors}
                onDragStart={handleSlidesDragStart}
                onDragEnd={handleSlidesDragEnd}
                onDragCancel={handleSlidesDragCancel}
              >
                <SortableContext
                  items={mainSlides
                    .map((s) => s.id)
                    .filter((id): id is string => id !== undefined)}
                  strategy={horizontalListSortingStrategy}
                >
                  <FlexContainer
                    gap={8}
                    justify="flex-start"
                    align="stretch"
                    style={{ marginBottom: 24, overflowX: 'auto' }}
                  >
                    {mainSlides.map((slide, index) => (
                      <SlideTimelineCard
                        key={slide.id}
                        slide={slide}
                        index={index}
                        isSelected={
                          currentSlideIdx === index && !isEditingPopUpSlide
                        }
                        isSortable={true}
                        onSelect={() => {
                          setCurrentSlideIdx(index);
                          setIsEditingPopUpSlide(false);
                          setSelectedSlideId(null);
                        }}
                        onDelete={() => deleteSlide(slide.id)}
                        isAnyDragging={isAnyDragging}
                      />
                    ))}

                    <AddSlideCard
                      direction="column"
                      justify="center"
                      align="center"
                      onClick={() => addSlide(false)}
                    >
                      <div className="add-icon">+</div>
                      {t('content.add.slide')}
                    </AddSlideCard>
                  </FlexContainer>
                </SortableContext>
              </DndContext>

              <TimelineLabel>{t('content.popup.slides')}</TimelineLabel>
              <FlexContainer
                gap={8}
                justify="flex-start"
                align="stretch"
                style={{ overflowX: 'auto' }}
              >
                {popUpSlides.map((slide, index) => (
                  <SlideTimelineCard
                    key={slide.id}
                    slide={slide}
                    index={index}
                    isSelected={
                      selectedSlideId === slide.id && isEditingPopUpSlide
                    }
                    isSortable={false}
                    onSelect={() => {
                      if (slide.id) {
                        setSelectedSlideId(slide.id);
                        setIsEditingPopUpSlide(true);
                      }
                    }}
                    onDelete={() => deleteSlide(slide.id || '')}
                    isAnyDragging={isAnyDragging}
                  />
                ))}
                <AddSlideCard
                  direction="column"
                  justify="center"
                  align="center"
                  onClick={() => addSlide(true)}
                >
                  <div className="add-icon">+</div>
                  {t('content.add.slide')}
                </AddSlideCard>
              </FlexContainer>
            </div>
          </TimelineContainer>
        </>
      )}

      <UnsavedChangesModal
        visible={showUnsavedChangesModal}
        hasErrors={isSaveDisabled}
        message={t('content.quiz.unsavedChanges.confirm')}
        onHide={() => setShowUnsavedChangesModal(false)}
        onSave={handleSave}
        onDiscard={handleDiscard}
      />
    </EditorModalDialog>
  );
};
